import { supabase } from '@/lib/supabase';
import { newId } from '@/lib/id';

export type Attachment = {
  path: string;
  name: string;
  mime: string;
  size: number;
};

/** רוחב מקסימלי לתמונה שמועלית, כדי לחסוך נפח ולהעלות מהר ברשת סלולרית. */
export const MAX_IMAGE_WIDTH = 1600;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

export const isImage = (mime: string) => mime.startsWith('image/');

/**
 * מקטין תמונה לפני העלאה וממיר ל WebP (מסמך התכנון 5.4).
 * קובץ שאינו תמונה, או תמונה שההמרה שלה נכשלה, מועלה כמו שהוא.
 */
export async function prepareForUpload(file: File): Promise<File> {
  if (!isImage(file.type) || file.type === 'image/gif') return file;

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_IMAGE_WIDTH / bitmap.width);

    // כבר קטנה ולא כבדה: אין טעם להמיר
    if (scale === 1 && file.size < 400_000) {
      bitmap.close();
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);

    const context = canvas.getContext('2d');
    if (!context) {
      bitmap.close();
      return file;
    }
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/webp', 0.82),
    );
    if (!blob || blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, '') + '.webp';
    return new File([blob], name, { type: 'image/webp' });
  } catch {
    return file;
  }
}

/**
 * Supabase Storage דוחה מפתח שאינו ASCII ב InvalidKey, וקובץ בעברית
 * הוא המקרה הרגיל כאן. לכן המפתח הוא uuid וסיומת בלבד, והשם המקורי
 * נשמר במטא דאטה של ההודעה ומוצג לעובד.
 */
export function storageKey(channelId: string, fileName: string, mime: string): string {
  const fromName = /\.([A-Za-z0-9]{1,8})$/.exec(fileName)?.[1];
  const fromMime = /^[\w-]+\/([\w.+-]+)$/.exec(mime)?.[1];
  const extension = (fromName ?? fromMime ?? 'bin').toLowerCase().replace(/[^a-z0-9]/g, '');
  return `${channelId}/${newId()}.${extension}`;
}

export async function uploadAttachment(
  channelId: string,
  file: File,
): Promise<Attachment> {
  const prepared = await prepareForUpload(file);
  if (prepared.size > MAX_FILE_BYTES) {
    throw new Error('הקובץ גדול מדי. הגבול הוא 10MB.');
  }

  const path = storageKey(channelId, prepared.name, prepared.type);
  const { error } = await supabase.storage.from('chat').upload(path, prepared, {
    contentType: prepared.type,
    upsert: false,
  });
  if (error) throw error;

  return { path, name: prepared.name, mime: prepared.type, size: prepared.size };
}

/**
 * הבדלי פרטי, ולכן כל צפייה היא דרך Signed URL קצר טווח.
 */
export async function signedUrls(paths: string[]): Promise<Record<string, string>> {
  if (paths.length === 0) return {};
  const { data, error } = await supabase.storage.from('chat').createSignedUrls(paths, 3600);
  if (error) throw error;

  const pairs: [string, string][] = [];
  for (const item of data ?? []) {
    if (item.path && item.signedUrl) pairs.push([item.path, item.signedUrl]);
  }
  return Object.fromEntries(pairs);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} בייט`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
